import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Three states for `user`:
 *   undefined → still checking the existing session
 *   null      → checked, not authenticated
 *   object    → authenticated user
 */
const AuthContext = createContext({
  user: undefined,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
      return me;
    } catch (_) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const u = await api.login({ email, password });
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const u = await api.register({ email, password, name });
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (_) {
      /* ignore */
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
